import { useEffect, useState } from 'react';
import { useCopyToClipboard } from '@/hooks/useCopyToClipboard';
import { siteMeta } from '@/mocks/homeData';

const NAV_ITEMS = [
  { label: '最新入口', href: '#entrances' },
  { label: '实时状态', href: '#status' },
  { label: '使用指南', href: '#guide' },
  { label: '防走丢手册', href: '#prevent' },
  { label: '常见问题', href: '#faq' },
];

export default function SiteNav() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { copiedId, copy } = useCopyToClipboard();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [menuOpen]);

  const isCopied = copiedId === 'nav-site';

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        scrolled || menuOpen
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200'
          : 'bg-transparent border-b border-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-10 flex items-center justify-between h-16 md:h-20">
        <a href="#top" className="flex items-center gap-3 group cursor-pointer">
          <span className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-500 text-background-50 text-xl shrink-0 transition-transform duration-300 group-hover:-rotate-6">
            <i className="ri-link" aria-hidden="true" />
          </span>
          <span className="flex flex-col leading-none">
            <span className="font-heading text-lg md:text-xl font-bold tracking-tight text-foreground-950">
              {siteMeta.name}
            </span>
            <span className="hidden sm:block mt-1 text-[11px] font-label tracking-[0.18em] text-secondary-700 uppercase">
              Latest Access Hub
            </span>
          </span>
        </a>

        <nav className="hidden md:flex items-center gap-1" aria-label="页面导航">
          {NAV_ITEMS.map((item) => (
            <a
              key={item.href}
              href={item.href}
              className="px-3.5 py-2 text-sm font-label text-foreground-700 hover:text-primary-700 hover:bg-primary-50 rounded-md transition-colors duration-200 cursor-pointer whitespace-nowrap"
            >
              {item.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => copy(siteMeta.shareUrl, 'nav-site')}
            className={`hidden lg:inline-flex items-center gap-2 px-4 py-2.5 rounded-md text-sm font-label whitespace-nowrap cursor-pointer transition-colors duration-200 ${
              isCopied
                ? 'bg-accent-500 text-background-50'
                : 'bg-primary-500 text-background-50 hover:bg-primary-600'
            }`}
          >
            <i className={isCopied ? 'ri-check-line' : 'ri-file-copy-line'} aria-hidden="true" />
            {isCopied ? '已复制本站' : '复制本站地址'}
          </button>

          <button
            type="button"
            aria-label="打开导航菜单"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((prev) => !prev)}
            className="md:hidden w-10 h-10 flex items-center justify-center rounded-md border border-background-300 text-foreground-800 cursor-pointer"
          >
            <i className={menuOpen ? 'ri-close-line text-xl' : 'ri-menu-line text-xl'} aria-hidden="true" />
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="md:hidden border-t border-background-200 bg-background-50 px-4 pt-4 pb-6 animate-fade-in">
          <nav className="flex flex-col gap-1" aria-label="移动端导航">
            {NAV_ITEMS.map((item) => (
              <a
                key={item.href}
                href={item.href}
                onClick={() => setMenuOpen(false)}
                className="flex items-center justify-between px-3 py-3 rounded-md text-sm font-label text-foreground-800 bg-background-100 cursor-pointer"
              >
                {item.label}
                <i className="ri-arrow-right-s-line text-foreground-500 text-lg" aria-hidden="true" />
              </a>
            ))}
          </nav>
          <button
            type="button"
            onClick={() => copy(siteMeta.shareUrl, 'nav-site')}
            className={`mt-3 w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-md text-sm font-label whitespace-nowrap cursor-pointer ${
              isCopied ? 'bg-accent-500 text-background-50' : 'bg-primary-500 text-background-50'
            }`}
          >
            <i className={isCopied ? 'ri-check-line' : 'ri-file-copy-line'} aria-hidden="true" />
            {isCopied ? '已复制本站' : '复制本站地址'}
          </button>
        </div>
      )}
    </header>
  );
}